import { iamApi, MODULE_URLS, getAuthCredentials, ApiError } from './client'
export { KNOWN_ROLES } from '@/utils/permissions'
import type { CreateOrgPayload, OrgType, Industry } from './iam'

export interface IAMOrganizationBasicInformation {
  id: string
  name: string
  description: string
  industry: Industry | string
  typeOfOrganization: OrgType | string
}

export interface IAMOrganizationContactInformation {
  address: string
  city: string
  state: string
  country: string
  postalCode: string
  phoneNumber: string
}

export interface IAMOrganization {
  basicInformation: IAMOrganizationBasicInformation
  contactInformation?: IAMOrganizationContactInformation
  administrativeInformation?: {
    dateOfIncorporation?: string
    gst?: string
    pan?: string
    registrationNumber?: string
    websiteUrl?: string
  }
  enable2fa?: boolean
  subscribedServices?: string[]
  ticketModuleType?: string
  status?: 'active' | 'inactive' | string
}

export interface IAMUser {
  userName: string
  firstName: string
  lastName: string
  emailAddress: string
  whatsappNumber?: string
  department?: string
  description?: string
  clientName?: string
  mobile?: string
  jobTitle?: string
  orgId?: string
  roleName?: string[]
  status?: string
  groupId?: number[]
  groupName?: string[]
  groupRoles?: string[]
  source?: string
  sourceLabel?: string
  federated?: boolean
  identityProvider?: string
  // True if this user's org membership is stamped with an LDAP identity
  // provider, independent of Keycloak's current live federation state (which
  // clears the moment a user/provider is removed, converting them to a plain
  // local account). Used to tell apart "genuinely always-local" users from
  // "LDAP-managed, temporarily showing as local" ones — the latter never had
  // a real password stored here, so reactivating them locally isn't safe.
  ldapManaged?: boolean
}

export interface CreateIAMUserRequest {
  userName: string
  firstName: string
  lastName: string
  emailAddress: string
  whatsappNumber?: string
  password: string
  department?: string
  description?: string
  clientName?: string
  orgId?: string
  roleName?: string[]
  grpId?: number[]
}

export interface UpdateIAMUserRequest {
  userName: string
  firstName: string
  lastName: string
  emailAddress: string
  whatsappNumber?: string
  department?: string
  description?: string
  clientName?: string
  orgId?: string
  roleName?: string[]
  grpId?: number[]
}

export interface IAMGroup {
  createTime?: string
  updateTime?: string
  description?: string
  groupId?: number
  name?: string
  organisationId?: string
  roles?: string[]
  serviceName?: string[]
  users?: string[]
  usersWithFullName?: Array<{
    emailAddress?: string
    name?: string
    roles?: string[]
  }>
}

export interface CreateIAMGroupRequest {
  name: string
  description?: string
  roles?: string[]
  serviceName?: string[]
  users?: string[]
}

export interface UpdateIAMGroupRequest {
  name?: string
  description?: string
  roles?: string[]
  serviceName?: string[]
  users?: string[]
}

export interface IAMDepartment {
  departmentId?: number
  departmentName: string
  description?: string
}

export interface ListEnvelope<T> {
  data?: T[]
  nextOffset?: number
  totalRecords?: number
}

export interface IAMUserListParams {
  organizationId?: string
  allOrganizations?: boolean
  search?:  string
  offset?:  number
  limit?:   number
  listAll?: boolean
}

function unwrapList<T>(response: T[] | ListEnvelope<T> | null | undefined): T[] {
  if (Array.isArray(response)) return response
  if (response?.data && Array.isArray(response.data)) return response.data
  return []
}

function unwrapEnvelope<T>(response: T[] | ListEnvelope<T> | null | undefined): ListEnvelope<T> {
  if (Array.isArray(response)) return { data: response }
  return (response as ListEnvelope<T>) ?? { data: [] }
}

function toQuery(path: string, params: Record<string, string | number | boolean | undefined>) {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === '') return
    search.set(key, String(value))
  })
  const qs = search.toString()
  return qs ? `${path}?${qs}` : path
}

async function getDepartmentsForOrg(orgId?: string): Promise<IAMDepartment[]> {
  const { token, orgId: currentOrgId } = getAuthCredentials()
  const effectiveOrgId = orgId || currentOrgId
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`
  if (effectiveOrgId) {
    headers.organisationID = effectiveOrgId
    headers.organizationId = effectiveOrgId
    headers['X-Org-Id'] = effectiveOrgId
  }
  const res = await fetch(`${MODULE_URLS.iam}${toQuery('/api/v1/iam/department', { listType: true })}`, { method: 'GET', headers })
  const text = await res.text()
  const data = text.trim() ? JSON.parse(text) : undefined
  if (!res.ok) {
    const message = data?.message ?? data?.error ?? 'Failed to load departments'
    if (res.status === 401 || res.status === 403 || /not allowed|forbidden|unauthorized|permission/i.test(message)) {
      return getDepartmentsFromOrgUsers(headers)
    }
    throw new ApiError(res.status, String(data?.code ?? 'UNKNOWN'), message)
  }
  return unwrapList(data)
}

async function getDepartmentsFromOrgUsers(headers: Record<string, string>): Promise<IAMDepartment[]> {
  const res = await fetch(`${MODULE_URLS.iam}${toQuery('/api/v1/iam/users', { listAll: true })}`, { method: 'GET', headers })
  const text = await res.text()
  const data = text.trim() ? JSON.parse(text) : undefined
  if (!res.ok) return []
  const names = new Set(
    unwrapEnvelope<IAMUser>(data).data
      ?.map(user => user.department?.trim())
      .filter((name): name is string => Boolean(name)) ?? [],
  )
  return Array.from(names)
    .sort((a, b) => a.localeCompare(b))
    .map(departmentName => ({ departmentName }))
}

export const iamAdminApi = {
  createOrganization: (payload: CreateOrgPayload & { basicInformation: CreateOrgPayload['basicInformation'] & { id: string } }) =>
    iamApi.post<IAMOrganization>('/api/v1/iam/organizations', payload),
  listOrganizations: async () =>
    unwrapList(await iamApi.get<IAMOrganization[] | ListEnvelope<IAMOrganization>>(toQuery('/api/v1/iam/organizations', { listAll: true }))),
  getOrganization: (organizationId: string) =>
    iamApi.get<IAMOrganization>(`/api/v1/iam/organizations/${encodeURIComponent(organizationId)}`),
  updateOrganization: (organizationId: string, payload: IAMOrganization) =>
    iamApi.put<IAMOrganization>(`/api/v1/iam/organizations/${encodeURIComponent(organizationId)}`, payload),
  deleteOrganization: (organizationId: string) =>
    iamApi.delete<void>(`/api/v1/iam/organizations/${encodeURIComponent(organizationId)}`),
  // Superadmin-only; deactivating an org cascades to disabling every
  // member's Keycloak login. Returns the full org (status included), same
  // shape as getOrganization/updateOrganization.
  toggleOrganizationActive: (organizationId: string) =>
    iamApi.put<IAMOrganization>(`/api/v1/iam/organizations/${encodeURIComponent(organizationId)}/toggle-active`, {}),

  // Paginated + searchable user list. Pass listAll:true only for dropdown/select use-cases.
  listUsers: async (params: IAMUserListParams = {}) => {
    const { search, offset = 0, limit = 25, listAll, organizationId, allOrganizations } = params
    const query = listAll
      ? toQuery('/api/v1/iam/users', { listAll: true, organizationId, allOrganizations })
      : toQuery('/api/v1/iam/users', { search, 'filter[search]': search, offset, limit, organizationId, allOrganizations })
    const res = await iamApi.get<IAMUser[] | ListEnvelope<IAMUser>>(query)
    return unwrapEnvelope(res)
  },
  createUser: (payload: CreateIAMUserRequest) =>
    iamApi.post<IAMUser>('/api/v1/iam/users', payload),
  updateUser: (userName: string, payload: UpdateIAMUserRequest) =>
    iamApi.put<IAMUser>(`/api/v1/iam/users/${encodeURIComponent(userName)}`, payload),
  deleteUser: (userName: string) =>
    iamApi.delete<void>(`/api/v1/iam/users/${encodeURIComponent(userName)}`),
  toggleUserActive: (userName: string) =>
    iamApi.put<IAMUser>(`/api/v1/iam/users/${encodeURIComponent(userName)}/toggle-active`, {}),

  listGroups: async () =>
    unwrapList(await iamApi.get<IAMGroup[] | ListEnvelope<IAMGroup>>(toQuery('/api/v1/iam/groups', { listType: true }))),
  createGroup: (payload: CreateIAMGroupRequest) =>
    iamApi.post<IAMGroup>('/api/v1/iam/groups', payload),
  updateGroup: (groupId: number, payload: UpdateIAMGroupRequest) =>
    iamApi.put<IAMGroup>(`/api/v1/iam/groups/${groupId}`, payload),
  deleteGroup: (groupId: number) =>
    iamApi.delete<void>(`/api/v1/iam/groups/${groupId}`),
  addUsersToGroup: (groupId: number, userIds: string[]) =>
    iamApi.post<IAMGroup>(`/api/v1/iam/groups/${groupId}/users`, { userIds }),
  listGroupUsers: (groupId: number) =>
    iamApi.get<IAMGroup>(`/api/v1/iam/groups/${groupId}`),

  listDepartments: (orgId?: unknown) => getDepartmentsForOrg(typeof orgId === 'string' ? orgId : undefined),
  createDepartment: (payload: IAMDepartment) =>
    iamApi.post<IAMDepartment>('/api/v1/iam/department', payload),
  updateDepartment: (departmentId: number, payload: IAMDepartment) =>
    iamApi.put<IAMDepartment>(`/api/v1/iam/department/${departmentId}`, payload),
  deleteDepartment: (departmentId: number) =>
    iamApi.delete<void>(`/api/v1/iam/department/${departmentId}`),

  // Org-wide MFA requirement (applies Keycloak's OTP-required role to every user in the org)
  fetch2fa: () =>
    iamApi.get<{ enable2fa: boolean }>('/api/v1/iam/enable2fa'),
  enable2fa: (enable2fa: boolean) =>
    iamApi.post<{ enable2fa: boolean }>('/api/v1/iam/enable2fa', { enable2fa }),

  // Current user's active Keycloak sessions
  listMySessions: () =>
    iamApi.get<IAMUserSession[]>('/api/v1/iam/sessions'),
  revokeSession: (sessionId: string) =>
    iamApi.delete<void>(`/api/v1/iam/sessions/${encodeURIComponent(sessionId)}`),
  revokeAllSessions: () =>
    iamApi.delete<void>('/api/v1/iam/sessions'),
}

export interface IAMUserSession {
  id?: string
  ipAddress?: string
  start?: string
  lastAccess?: string
  clients?: string[]
}
